import { type ReactElement } from 'react';
import NoIndex from '../components/NoIndex';

/**
 * Allgemeine Geschäftsbedingungen (AGB).
 *
 * WICHTIG / rechtlicher Hinweis:
 * Dies ist ein sorgfältig formuliertes Grundgerüst, das die typischen
 * Risiken eines Buchhaltungs-SaaS adressiert (Haftung, Steuer-Verantwortung,
 * keine Steuerberatung). Es ersetzt KEINE anwaltliche Prüfung.
 *
 * Bewusst KEIN pauschaler Haftungsausschluss: Ein solcher wäre nach
 * §§ 305 ff., 309 Nr. 7 BGB unwirksam und würde nach § 306 BGB die volle
 * gesetzliche Haftung auslösen. Stattdessen abgestufte Haftung + klare
 * Abgrenzung der Nutzerverantwortung (das schützt z. B. bei fehlerhaften
 * vom Nutzer eingegebenen Steuerdaten).
 *
 * B2C-sicher formuliert (deckt damit auch B2B ab). Enthält daher auch eine
 * Widerrufsbelehrung für Verbraucher.
 */
export default function AGB(): ReactElement {
  return (
    <div className="pt-24 pb-16 lg:pt-32 lg:pb-24">
      <NoIndex />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-secondary-900 mb-8">
          Allgemeine Geschäftsbedingungen (AGB)
        </h1>

        <div className="max-w-none space-y-8 text-secondary-700 leading-relaxed">
          {/* 1. Geltungsbereich & Anbieter */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              1. Geltungsbereich und Anbieter
            </h2>
            <p>
              Diese AGB gelten für die Nutzung der Software „Saldify" (nachfolgend
              „Software" oder „Dienst"), die bereitgestellt wird von:
            </p>
            <p className="mt-2">
              CuiveMedia, Inhaber Brian Brelöer, Siriusstraße 2, 12524 Berlin
              (nachfolgend „Anbieter").
            </p>
            <p className="mt-2">
              Sie gelten gegenüber Unternehmern (§ 14 BGB) ebenso wie gegenüber
              Verbrauchern (§ 13 BGB). Abweichende Bedingungen des Nutzers werden nicht
              Vertragsbestandteil, es sei denn, der Anbieter stimmt ihrer Geltung
              ausdrücklich zu.
            </p>
          </section>

          {/* 2. Leistungsgegenstand */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              2. Leistungsgegenstand
            </h2>
            <p>
              Saldify ist eine cloudbasierte Software zur Unterstützung der Buchhaltung,
              insbesondere für Rechnungen, Belege, Banking-Abgleich, Auswertungen und die
              Vorbereitung steuerlicher Aufstellungen. Der Anbieter stellt die Software als
              Werkzeug zur Verfügung; die inhaltliche Nutzung, Dateneingabe und
              Entscheidung über die Verwendung der Ergebnisse obliegt allein dem Nutzer.
            </p>
            <p className="mt-2">
              Der Anbieter schuldet die Bereitstellung der Software in ihrer jeweils
              aktuellen Fassung, nicht jedoch einen bestimmten wirtschaftlichen oder
              steuerlichen Erfolg.
            </p>
          </section>

          {/* 3. KEINE Steuer- oder Rechtsberatung */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              3. Keine Steuer-, Rechts- oder Buchführungsberatung
            </h2>
            <p>
              Die Software stellt <strong>keine Steuerberatung, Rechtsberatung oder
              geschäftsmäßige Hilfeleistung in Steuersachen</strong> im Sinne des
              Steuerberatungsgesetzes (StBerG) dar. Vom Dienst bereitgestellte Hinweise,
              Berechnungen, Vorschläge oder Auswertungen sind automatisiert erzeugte
              Hilfestellungen und ersetzen nicht die individuelle Beratung durch einen
              Steuerberater, Rechtsanwalt oder eine andere zur Beratung befugte Person.
            </p>
            <p className="mt-2">
              Der Nutzer ist gehalten, in Zweifelsfällen sowie vor der Übermittlung von
              Erklärungen an Finanzbehörden oder Dritte fachkundigen Rat einzuholen.
            </p>
          </section>

          {/* 4. Verantwortung des Nutzers */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              4. Verantwortung des Nutzers für Daten und Erklärungen
            </h2>
            <p>
              Der Nutzer ist allein verantwortlich für die Richtigkeit, Vollständigkeit und
              Aktualität der von ihm eingegebenen oder importierten Daten. Dies gilt
              insbesondere für Buchungs-, Beleg-, Umsatz- und Stammdaten sowie für alle
              Angaben, die Grundlage steuerlicher oder rechtlicher Erklärungen sind.
            </p>
            <p className="mt-2">
              Der Nutzer ist ferner allein verantwortlich für die Prüfung der vom Dienst
              erzeugten Ergebnisse vor deren Verwendung sowie für die fristgerechte und
              inhaltlich zutreffende Übermittlung von Erklärungen, Meldungen und Zahlungen
              an Finanzbehörden oder sonstige Stellen. Der Anbieter übernimmt keine Gewähr
              dafür, dass auf Basis der eingegebenen Daten erzeugte Auswertungen oder
              Vorbereitungen steuerlich oder rechtlich zutreffend, vollständig oder für den
              konkreten Zweck des Nutzers geeignet sind.
            </p>
            <p className="mt-2">
              Der Nutzer ist verpflichtet, regelmäßig Sicherungskopien der von ihm
              eingestellten Daten zu erstellen, soweit dies für ihn von Bedeutung ist.
            </p>
          </section>

          {/* 5. Verfügbarkeit */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              5. Verfügbarkeit
            </h2>
            <p>
              Der Anbieter bemüht sich um eine möglichst unterbrechungsfreie Verfügbarkeit
              der Software. Ein Anspruch auf ununterbrochene Verfügbarkeit besteht nicht.
              Wartungsarbeiten, Störungen außerhalb des Einflussbereichs des Anbieters
              (z. B. höhere Gewalt, Ausfälle von Vorleistern) sowie notwendige
              Sicherheitsmaßnahmen können die Verfügbarkeit vorübergehend einschränken.
            </p>
          </section>

          {/* 6. Vertragslaufzeit / Kündigung / Preise */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              6. Vergütung, Laufzeit und Kündigung
            </h2>
            <p>
              Die jeweils gültigen Preise und Tarife ergeben sich aus der Preisübersicht
              zum Zeitpunkt der Buchung. Kostenpflichtige Abonnements verlängern sich um den
              jeweils gewählten Abrechnungszeitraum, sofern sie nicht fristgerecht gekündigt
              werden. Die Kündigung ist über das Kundenkonto bzw. das Kundenportal möglich.
              Das Recht zur außerordentlichen Kündigung aus wichtigem Grund bleibt
              unberührt.
            </p>
          </section>

          {/* 7. HAFTUNG (abgestuft, wirksam) */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              7. Haftung
            </h2>
            <p>
              Der Anbieter haftet unbeschränkt für Schäden aus der Verletzung des Lebens,
              des Körpers oder der Gesundheit, die auf einer Pflichtverletzung des Anbieters
              beruhen, sowie für Schäden, die auf Vorsatz oder grober Fahrlässigkeit des
              Anbieters beruhen, ferner nach Maßgabe des Produkthaftungsgesetzes und im
              Umfang einer übernommenen Garantie.
            </p>
            <p className="mt-2">
              Bei der leicht fahrlässigen Verletzung einer wesentlichen Vertragspflicht
              (einer Pflicht, deren Erfüllung die ordnungsgemäße Durchführung des Vertrags
              überhaupt erst ermöglicht und auf deren Einhaltung der Nutzer regelmäßig
              vertrauen darf – „Kardinalpflicht") ist die Haftung des Anbieters auf den bei
              Vertragsschluss vorhersehbaren, vertragstypischen Schaden begrenzt.
            </p>
            <p className="mt-2">
              Im Übrigen ist die Haftung des Anbieters ausgeschlossen. Insbesondere haftet
              der Anbieter nicht für Schäden, die darauf beruhen, dass der Nutzer
              unrichtige, unvollständige oder verspätete Daten eingegeben, die vom Dienst
              erzeugten Ergebnisse ungeprüft verwendet oder seine Pflichten gegenüber
              Finanzbehörden oder Dritten nicht ordnungsgemäß erfüllt hat.
            </p>
            <p className="mt-2">
              Die vorstehenden Haftungsbeschränkungen gelten auch zugunsten der
              gesetzlichen Vertreter und Erfüllungsgehilfen des Anbieters.
            </p>
          </section>

          {/* 8. Widerruf (Verbraucher) */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              8. Widerrufsrecht für Verbraucher
            </h2>
            <p>
              Verbrauchern steht ein gesetzliches Widerrufsrecht zu. Einzelheiten ergeben
              sich aus der gesonderten Widerrufsbelehrung, die dem Nutzer im Bestellprozess
              zur Verfügung gestellt wird. Bei digitalen Inhalten/Dienstleistungen kann das
              Widerrufsrecht vorzeitig erlöschen, wenn der Verbraucher ausdrücklich
              zustimmt, dass mit der Ausführung vor Ablauf der Widerrufsfrist begonnen wird,
              und seine Kenntnis vom Erlöschen des Widerrufsrechts bestätigt.
            </p>
          </section>

          {/* 9. Datenschutz */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              9. Datenschutz
            </h2>
            <p>
              Informationen zur Verarbeitung personenbezogener Daten finden Sie in unserer{' '}
              <a href="/datenschutz" className="text-primary-600 hover:underline">
                Datenschutzerklärung
              </a>
              .
            </p>
          </section>

          {/* 10. Schlussbestimmungen */}
          <section>
            <h2 className="text-xl font-semibold text-secondary-900 mb-3">
              10. Schlussbestimmungen
            </h2>
            <p>
              Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des
              UN-Kaufrechts; bei Verbrauchern bleiben zwingende Verbraucherschutzvorschriften
              ihres Aufenthaltsstaats unberührt. Sollte eine Bestimmung dieser AGB unwirksam
              sein, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt; an die Stelle
              der unwirksamen Bestimmung tritt die gesetzliche Regelung.
            </p>
          </section>

          <p className="text-sm text-secondary-500 pt-4 border-t border-secondary-200">
            Diese AGB stellen ein Grundgerüst dar und sollten vor dem Einsatz anwaltlich
            geprüft werden.
          </p>
        </div>
      </div>
    </div>
  );
}
